/**
 * Rig Context - Multi-rig state management
 * 
 * Manages multiple rigs (collections of devices) with localStorage persistence.
 * Maximum 5 rigs allowed to manage storage space.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { trackEvent, AnalyticsEvents } from '../services/analytics';

const STORAGE_KEY = 'astropower-rigs';
const LEGACY_STORAGE_KEY = 'astropower-rig';
const MAX_RIGS = 5;

export interface Rig {
  id: string;
  name: string;
  devices: any[]; // Device[] - using any to avoid circular import
  createdAt: number;
  updatedAt: number;
}

interface RigContextType {
  rigs: Rig[];
  activeRigId: string | null;
  activeRig: Rig | null;
  createRig: (name: string) => string;
  updateRigName: (rigId: string, name: string) => void;
  deleteRig: (rigId: string) => void;
  setActiveRig: (rigId: string) => void;
  updateRigDevices: (rigId: string, devices: any[]) => void;
  canCreateRig: boolean;
}

const RigContext = createContext<RigContextType | undefined>(undefined);

export const useRigs = (): RigContextType => {
  const context = useContext(RigContext);
  if (!context) {
    throw new Error('useRigs must be used within a RigProvider');
  }
  return context;
};

interface RigProviderProps {
  children: ReactNode;
}

export const RigProvider: React.FC<RigProviderProps> = ({ children }) => {
  const [rigs, setRigs] = useState<Rig[]>(() => {
    try {
      // Check for new multi-rig format
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }

      // Migrate from legacy single-rig format
      const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (legacy) {
        const devices = JSON.parse(legacy);
        const initialRig: Rig = {
          id: 'rig-1',
          name: 'Rig 1',
          devices: devices,
          createdAt: Date.now(),
          updatedAt: Date.now()
        };
        
        // Save to new format and remove old key
        localStorage.setItem(STORAGE_KEY, JSON.stringify([initialRig]));
        localStorage.removeItem(LEGACY_STORAGE_KEY);
        
        return [initialRig];
      }
    } catch (error) {
      console.error('Failed to load rigs from localStorage:', error);
    }
    return [];
  });

  const [activeRigId, setActiveRigId] = useState<string | null>(() => {
    if (rigs.length > 0) {
      return rigs[0].id;
    }
    return null;
  });

  // Save to localStorage whenever rigs change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(rigs));
    } catch (error) {
      console.error('Failed to save rigs to localStorage:', error);
    }
  }, [rigs]);

  // Ensure activeRigId is valid
  useEffect(() => {
    if (activeRigId && !rigs.find(r => r.id === activeRigId)) {
      setActiveRigId(rigs.length > 0 ? rigs[0].id : null);
    }
  }, [rigs, activeRigId]);

  const createRig = useCallback((name: string): string => {
    if (rigs.length >= MAX_RIGS) {
      throw new Error(`Maximum ${MAX_RIGS} rigs allowed`);
    }

    const newRig: Rig = {
      id: `rig-${Date.now()}`,
      name: name.trim() || `Rig ${rigs.length + 1}`,
      devices: [],
      createdAt: Date.now(),
      updatedAt: Date.now()
    };

    setRigs(prev => [...prev, newRig]);
    setActiveRigId(newRig.id);
    
    // Track analytics
    trackEvent({ eventType: AnalyticsEvents.RIG_CREATED });
    
    return newRig.id;
  }, [rigs.length]);

  const updateRigName = useCallback((rigId: string, name: string) => {
    setRigs(prev => prev.map(r => 
      r.id === rigId 
        ? { ...r, name: name.trim() || r.name, updatedAt: Date.now() }
        : r
    ));
    
    // Track analytics
    trackEvent({ eventType: AnalyticsEvents.RIG_RENAMED });
  }, []);

  const deleteRig = useCallback((rigId: string) => {
    setRigs(prev => {
      const updated = prev.filter(r => r.id !== rigId);
      
      // If deleting active rig, switch to first available
      if (rigId === activeRigId) {
        setActiveRigId(updated.length > 0 ? updated[0].id : null);
      }
      
      // Track analytics
      trackEvent({ eventType: AnalyticsEvents.RIG_DELETED });
      
      return updated;
    });
  }, [activeRigId]);

  const setActiveRigCallback = useCallback((rigId: string) => {
    if (rigs.find(r => r.id === rigId)) {
      setActiveRigId(rigId);
    }
  }, [rigs]);

  const updateRigDevices = useCallback((rigId: string, devices: any[]) => {
    setRigs(prev => prev.map(r => 
      r.id === rigId 
        ? { ...r, devices, updatedAt: Date.now() }
        : r
    ));
  }, []);

  const activeRig = rigs.find(r => r.id === activeRigId) || null;

  const value: RigContextType = {
    rigs,
    activeRigId,
    activeRig,
    createRig,
    updateRigName,
    deleteRig,
    setActiveRig: setActiveRigCallback,
    updateRigDevices,
    canCreateRig: rigs.length < MAX_RIGS
  };

  return (
    <RigContext.Provider value={value}>
      {children}
    </RigContext.Provider>
  );
};
